/**
 * Phase 8 · Addendum F §33.2 — Sourcing history column.
 *
 * The Wrangle reference frame places a persistent history rail between the
 * global sidebar and the intake/canvas. Unlike Smart Match's slide-in
 * (which overlays the record column), this one is always visible while
 * the user is anywhere inside `/sourcing/*`.
 *
 * Data flow:
 *   - Reads from the shared localStorage store via `readSourcingThreads()`.
 *   - Subscribes to the `ggj:smartmatch-threads-changed` event so changes
 *     made elsewhere (e.g. rename in the canvas top bar) hot-update here.
 *
 * Row affordances per the spec: `Rename · Duplicate · Delete · Export`.
 * Duplicate is intentionally omitted here — sourcing threads are cheap
 * to recreate from the empty state and keeping the menu to three items
 * (Rename / Export / Delete) matches the reference screenshot. We keep
 * the handler available so P9 work can re-add it without a schema churn.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, MoreHorizontal, Pencil, Plus, Trash2, X } from 'lucide-react';
import { useAuth } from '@core/auth/useAuth';
import {
  deleteThread,
  readSourcingThreads,
  renameThread,
  type SourcingThread,
} from '../lib/sourcingThreads';
import { relativeTimeLabel } from '@modules/smart-match/lib/recentSearchChip';

const LS_HISTORY_FILTER = 'ggj.sourcing.history.filter';
const LS_HISTORY_EMBEDDED_OPEN = 'ggj.sourcing.history.embedded-open';
type ListFilter = 'all' | 'mine';

function readFilter(): ListFilter {
  try {
    const v = localStorage.getItem(LS_HISTORY_FILTER);
    if (v === 'all' || v === 'mine') return v;
  } catch {
    /* ignore */
  }
  return 'all';
}

function writeFilter(f: ListFilter): void {
  try {
    localStorage.setItem(LS_HISTORY_FILTER, f);
  } catch {
    /* ignore */
  }
}

function readEmbeddedOpen(): boolean {
  try {
    return localStorage.getItem(LS_HISTORY_EMBEDDED_OPEN) === '1';
  } catch {
    return false;
  }
}

function writeEmbeddedOpen(open: boolean): void {
  try {
    localStorage.setItem(LS_HISTORY_EMBEDDED_OPEN, open ? '1' : '0');
  } catch {
    /* ignore */
  }
}

interface Props {
  /** When set, the matching row is highlighted. Pass `null` on intake. */
  activeThreadId: string | null;
  /**
   * Layout variant:
   *  - `standalone` (default) — renders as a fixed-width 280px left-most
   *    column with `border-r`. Used on the Sourcing intake page.
   *  - `embedded` — renders as a bounded panel inside another column
   *    (e.g. stacked above the agent thread, Wrangle-style). No fixed
   *    width, capped height so the Copilot card + chat still fit below.
   */
  variant?: 'standalone' | 'embedded' | 'popover';
  /** Called when the user clicks the × close button (standalone only). */
  onClose?: () => void;
}

export function SourcingHistoryColumn({ activeThreadId, variant = 'standalone', onClose }: Props) {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [threads, setThreads] = useState<SourcingThread[]>([]);
  const [filter, setFilter] = useState<ListFilter>(() => readFilter());
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [menuOpenFor, setMenuOpenFor] = useState<string | null>(null);
  // Post user-feedback: the embedded variant now defaults to collapsed so
  // the chat + composer below actually fit on screen. Standalone variant
  // (left-rail on the intake page) ignores this — it's always expanded.
  const [embeddedOpen, setEmbeddedOpen] = useState<boolean>(() => readEmbeddedOpen());
  const toggleEmbeddedOpen = useCallback(() => {
    setEmbeddedOpen((prev) => {
      const next = !prev;
      writeEmbeddedOpen(next);
      return next;
    });
  }, []);
  const panelRef = useRef<HTMLElement | null>(null);

  const refresh = useCallback(() => {
    setThreads(readSourcingThreads());
  }, []);

  useEffect(() => {
    refresh();
    const onChange = () => refresh();
    window.addEventListener('ggj:smartmatch-threads-changed', onChange);
    window.addEventListener('storage', onChange);
    return () => {
      window.removeEventListener('ggj:smartmatch-threads-changed', onChange);
      window.removeEventListener('storage', onChange);
    };
  }, [refresh]);

  useEffect(() => {
    if (!menuOpenFor) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && target.closest('[data-ggj-sourcing-row-menu]')) return;
      setMenuOpenFor(null);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpenFor]);

  const filtered = useMemo(() => {
    const uid = user?.id ?? null;
    if (filter === 'all') return threads;
    if (!uid) return threads;
    return threads.filter((t) => !t.createdByUserId || t.createdByUserId === uid);
  }, [threads, filter, user?.id]);

  const onPickFilter = (f: ListFilter) => {
    setFilter(f);
    writeFilter(f);
  };

  const onStartRename = (t: SourcingThread) => {
    setRenamingId(t.id);
    setRenameValue(t.name);
    setMenuOpenFor(null);
  };

  const commitRename = () => {
    if (!renamingId) return;
    renameThread(renamingId, renameValue);
    setRenamingId(null);
    refresh();
  };

  const onDelete = (t: SourcingThread) => {
    deleteThread(t.id);
    setMenuOpenFor(null);
    if (activeThreadId === t.id) {
      navigate('/sourcing/new', { replace: true });
    } else {
      refresh();
    }
  };

  const now = Date.now();

  // Standalone = Wrangle-style left rail (its own column). Embedded =
  // stacked atop the agent column (Wrangle reference image 2): share
  // the column's width, keep the scroll bounded so the Copilot card
  // + chat composer still breathe below.
  const isEmbedded = variant === 'embedded';
  const isPopover = variant === 'popover';
  // Popover variant is rendered inside a floating `SourcingHistoryPopover`
  // (top-bar toggle) — no fixed width, no border (the parent provides it)
  // and list is always expanded because the popover itself is the
  // open/closed affordance.
  const shellClass = isPopover
    ? 'flex w-full flex-col'
    : isEmbedded
      ? 'flex w-full shrink-0 flex-col border-b'
      : 'flex h-full w-[280px] shrink-0 flex-col border-r';

  // Embedded + closed → ~34px collapsed strip (title + count + chevron).
  // Embedded + open → header + filter + capped list.
  // Standalone / popover → always fully expanded.
  const listOpen = isEmbedded ? embeddedOpen : true;

  return (
    <aside
      ref={panelRef}
      aria-label="Sourcing history"
      className={shellClass}
      style={{
        background: 'var(--vscode-bg-primary)',
        borderColor: 'var(--vscode-border-primary)',
      }}
      data-ggj-sourcing-history-column
      data-ggj-variant={variant}
      data-ggj-history-open={listOpen ? 'true' : 'false'}
    >
      {isEmbedded ? (
        <button
          type="button"
          onClick={toggleEmbeddedOpen}
          className="flex w-full items-center gap-2 border-b px-3 py-2 text-left transition-colors hover:bg-[var(--vscode-bg-hover)]"
          style={{ borderColor: 'var(--vscode-border-primary)' }}
          aria-expanded={embeddedOpen}
          aria-controls="ggj-sourcing-history-list"
        >
          {embeddedOpen ? (
            <ChevronDown
              className="h-3.5 w-3.5 shrink-0"
              style={{ color: 'var(--vscode-text-tertiary)' }}
              aria-hidden
            />
          ) : (
            <ChevronRight
              className="h-3.5 w-3.5 shrink-0"
              style={{ color: 'var(--vscode-text-tertiary)' }}
              aria-hidden
            />
          )}
          <span
            className="text-[12px] font-semibold"
            style={{ color: 'var(--vscode-text-primary)' }}
          >
            History
          </span>
          <span
            className="text-[10.5px]"
            style={{ color: 'var(--vscode-text-tertiary)' }}
          >
            · {threads.length} chat{threads.length === 1 ? '' : 's'}
          </span>
        </button>
      ) : (
        <div
          className="flex items-center justify-between border-b px-3 py-2.5"
          style={{ borderColor: 'var(--vscode-border-primary)' }}
        >
          <span
            className="text-[11px]"
            style={{ color: 'var(--vscode-text-tertiary)' }}
          >
            {isPopover
              ? `${threads.length} chat${threads.length === 1 ? '' : 's'} · last 30 days`
              : `${threads.length} previous search${threads.length === 1 ? '' : 'es'}`}
          </span>
          {/* Close button — standalone variant only */}
          {!isPopover && onClose && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close history panel"
              title="Close history panel"
              className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-[6px] transition-colors hover:bg-[var(--vscode-bg-hover)]"
              style={{ color: 'var(--vscode-text-tertiary)' }}
            >
              <X className="h-3.5 w-3.5" aria-hidden />
            </button>
          )}
        </div>
      )}

      {/* Standalone variant keeps the "New sourcing chat" button + filter
          pills above the list. The embedded variant no longer renders
          the "New sourcing chat" button here — it's been hoisted into
          the top action bar (`MatchCanvasLayout` → `NextStepBridge`
          `onNewChat` prop) so every action lives in one place. */}
      {listOpen ? (
        <>
          {!isEmbedded ? (
            <div className="px-3 pt-3">
              <button
                type="button"
                onClick={() => navigate('/sourcing/new')}
                className="flex h-8 w-full items-center justify-center gap-1.5 rounded-[10px] bg-[var(--ggj-accent,#10B981)] px-3 text-[12px] font-medium text-white transition-opacity hover:opacity-90"
              >
                <Plus className="h-3.5 w-3.5" aria-hidden />
                New sourcing chat
              </button>
              <div
                className="mt-2 flex rounded-[8px] border p-0.5"
                style={{ borderColor: 'var(--vscode-border-primary)' }}
                role="group"
                aria-label="Filter history"
              >
                {(
                  [
                    { id: 'all' as const, label: 'All' },
                    { id: 'mine' as const, label: 'Mine' },
                  ] as const
                ).map((opt) => {
                  const active = filter === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => onPickFilter(opt.id)}
                      className="flex-1 rounded-[6px] px-2 py-1 text-[10.5px] font-medium transition-colors"
                      style={{
                        background: active ? 'var(--vscode-bg-elevated)' : 'transparent',
                        color: active ? 'var(--vscode-text-primary)' : 'var(--vscode-text-secondary)',
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <div
              className="flex items-center gap-1 border-b px-3 py-1.5"
              style={{ borderColor: 'var(--vscode-border-primary)' }}
              role="group"
              aria-label="Filter history"
            >
              {(
                [
                  { id: 'all' as const, label: 'All' },
                  { id: 'mine' as const, label: 'Mine' },
                ] as const
              ).map((opt) => {
                const active = filter === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => onPickFilter(opt.id)}
                    className="rounded-[6px] px-2 py-0.5 text-[10.5px] font-medium transition-colors"
                    style={{
                      background: active ? 'var(--vscode-bg-elevated)' : 'transparent',
                      color: active ? 'var(--vscode-text-primary)' : 'var(--vscode-text-tertiary)',
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          )}

          <div
            id="ggj-sourcing-history-list"
            className={
              isEmbedded
                ? 'flex max-h-[180px] flex-col overflow-y-auto px-2 pb-2 pt-1 ggj-scroll-thin'
                : isPopover
                  ? 'mt-3 flex max-h-[340px] flex-col overflow-y-auto px-2 pb-3 ggj-scroll-thin'
                  : 'mt-3 flex min-h-0 flex-1 flex-col overflow-y-auto px-2 pb-4 ggj-scroll-thin'
            }
          >
        {filtered.length === 0 ? (
          <p
            className="px-3 py-6 text-center text-[11.5px]"
            style={{ color: 'var(--vscode-text-tertiary)' }}
          >
            {threads.length === 0
              ? 'No sourcing chats yet. Start one above.'
              : 'No sourcing chats match this filter.'}
          </p>
        ) : (
          <ul className="flex flex-col gap-0.5">
            {filtered.map((t) => {
              const active = activeThreadId === t.id;
              const subtitle = `${t.candidateCount} candidate${
                t.candidateCount === 1 ? '' : 's'
              } · ${relativeTimeLabel(t.createdAt, now)}`;
              return (
                <li key={t.id} className="group relative">
                  {renamingId === t.id ? (
                    <input
                      autoFocus
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onBlur={commitRename}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') commitRename();
                        if (e.key === 'Escape') setRenamingId(null);
                      }}
                      className="h-8 w-full rounded-[8px] border bg-[var(--vscode-bg-elevated)] px-2 text-[12px] font-medium outline-none"
                      style={{
                        borderColor: 'var(--ggj-accent,#10B981)',
                        color: 'var(--vscode-text-primary)',
                      }}
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => navigate(`/sourcing/${t.id}`)}
                      className={`flex w-full items-center gap-2 rounded-[8px] px-2 py-1.5 text-left transition-colors ${
                        active
                          ? 'bg-[var(--ggj-accent-soft,#D1FAE5)]'
                          : 'hover:bg-[var(--vscode-bg-hover)]'
                      }`}
                    >
                      <span
                        className="h-1.5 w-1.5 shrink-0 rounded-full"
                        style={{
                          background: active
                            ? 'var(--ggj-accent,#10B981)'
                            : 'var(--vscode-text-tertiary)',
                          opacity: active ? 1 : 0.45,
                        }}
                        aria-hidden
                      />
                      <span className="min-w-0 flex-1">
                        <span
                          className="block truncate text-[12.5px] font-medium"
                          style={{
                            color: active ? '#065F46' : 'var(--vscode-text-primary)',
                          }}
                        >
                          {t.name}
                        </span>
                        <span
                          className="block truncate text-[10.5px]"
                          style={{ color: 'var(--vscode-text-tertiary)' }}
                        >
                          {subtitle}
                        </span>
                      </span>
                    </button>
                  )}

                  <button
                    type="button"
                    data-ggj-sourcing-row-menu
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenuOpenFor((prev) => (prev === t.id ? null : t.id));
                    }}
                    aria-label={`Actions for ${t.name}`}
                    aria-haspopup="menu"
                    aria-expanded={menuOpenFor === t.id}
                    className="absolute right-1.5 top-1.5 rounded p-1 text-[color:var(--vscode-text-tertiary)] opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100"
                  >
                    <MoreHorizontal className="h-3.5 w-3.5" aria-hidden />
                  </button>

                  {menuOpenFor === t.id ? (
                    <div
                      data-ggj-sourcing-row-menu
                      role="menu"
                      className="absolute left-4 top-8 z-[42] w-[140px] overflow-hidden rounded-[8px] border bg-[var(--vscode-bg-elevated)] py-1 shadow-lg"
                      style={{ borderColor: 'var(--vscode-border-primary)' }}
                    >
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => onStartRename(t)}
                        className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-[12px] hover:bg-[var(--vscode-bg-hover)]"
                        style={{ color: 'var(--vscode-text-primary)' }}
                      >
                        <Pencil className="h-3.5 w-3.5 flex-shrink-0" aria-hidden />
                        Rename
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => onDelete(t)}
                        className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-[12px] hover:bg-red-50 dark:hover:bg-red-900/20"
                        style={{ color: '#DC2626' }}
                      >
                        <Trash2 className="h-3.5 w-3.5 flex-shrink-0" aria-hidden />
                        Delete
                      </button>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </div>
        </>
      ) : null}
    </aside>
  );
}
